import {
    IBalusterGeometry,
    IStairCalculationResult,
    IStairConfig,
    IStepGeometry,
    IStringerGeometry,
    IStringerType,
    ISupportPosts,
    IVec3,
} from '@stair-craft/common/util';

import { RISER_BOARD_THICKNESS } from '../../constants';
import { IProfilePoint, kosourProfile, kosourRunAxis, tetivaProfile } from '../shared-board-profile/board-profile';
import { IPlanPoint, planIntersect, planLength, planSub } from '../shared-plan-geometry/plan-geometry';
import { exitRiserExtent, riserExtent, riserSpanY, winderRiserBoardOutline } from '../shared-riser-board/riser-board';
import { treadFootprint } from '../shared-tread-footprint/tread-footprint';
import { IPartVertex, normalizeOutline, outlineKey, planToPart } from './part-outline';

/** The kinds of board a stair is cut from, in the order the archive lists them. */
export enum EPartKind {
    Tread = 'tread',
    Landing = 'landing',
    Riser = 'riser',
    Stringer = 'stringer',
    SupportPost = 'support-post',
}

const KIND_ORDER: readonly EPartKind[] = [EPartKind.Tread, EPartKind.Landing, EPartKind.Riser, EPartKind.Stringer, EPartKind.SupportPost];

/** A board to cut: its flat outline at the origin, the species, the thickness and how many alike. */
export interface IProductionPart {
    kind: EPartKind;
    materialId: string;
    thickness: number;
    outline: IPartVertex[];
    count: number;
}

type IRawPart = Omit<IProductionPart, 'count'>;

/** Parts thinner or narrower than this are not boards (mm). */
const MIN_SIZE: number = 1;

/** A rectangle `width` × `height` with its lower-left corner at the origin. */
function rectangle(width: number, height: number): IPartVertex[] {
    return [
        { x: 0, y: 0, bulge: 0 },
        { x: width, y: 0, bulge: 0 },
        { x: width, y: height, bulge: 0 },
        { x: 0, y: height, bulge: 0 },
    ];
}

/**
 * The outer edge of a spiral tread as an arc around the pole: the centre is where the two radial
 * edges meet, the bulge is tan(sweep / 4) of the arc from the first outline vertex to the second.
 */
function spiralOuterBulge(step: IStepGeometry, outline: readonly IPlanPoint[]): number {
    const corners: NonNullable<IStepGeometry['winderCorners']> | undefined = step.winderCorners;
    if (!corners || outline.length < 2) {
        return 0;
    }
    const center: IPlanPoint | null = planIntersect(
        { point: corners.innerBack, direction: planSub(corners.outerBack, corners.innerBack) },
        { point: corners.innerFront, direction: planSub(corners.outerFront, corners.innerFront) }
    );
    if (!center) {
        return 0;
    }
    const radius: number = planLength(planSub(corners.outerFront, center));
    const chord: number = planLength(planSub(outline[1], outline[0]));
    if (chord >= 2 * radius) {
        return 0;
    }
    const sweep: number = 2 * Math.asin(chord / (2 * radius));
    // The arc bows away from the centre: clockwise when the centre lies left of the chord.
    const along: IPlanPoint = planSub(outline[1], outline[0]);
    const toCenter: IPlanPoint = planSub(center, outline[0]);
    const centreLeft: boolean = along.x * toCenter.z - along.z * toCenter.x > 0;
    return (centreLeft ? -1 : 1) * Math.tan(sweep / 4);
}

function treadPart(step: IStepGeometry, config: IStairConfig): IRawPart {
    const footprint: IPlanPoint[] = treadFootprint(step, config.stringerType);
    const bulges: number[] = config.type === 'spiral' ? [spiralOuterBulge(step, footprint)] : [];
    return {
        kind: step.isLanding ? EPartKind.Landing : EPartKind.Tread,
        materialId: config.treadMaterialId,
        thickness: step.treadThickness,
        outline: normalizeOutline(planToPart(footprint, bulges)),
    };
}

/** The longest edge of a plan outline: the length of a riser board standing on it. */
function longestEdge(outline: readonly IPlanPoint[]): number {
    return Math.max(
        ...outline.map((point: IPlanPoint, index: number) => planLength(planSub(outline[(index + 1) % outline.length], point)))
    );
}

/** The riser boards of one step: the one under its tread and, behind the last tread, the closing one. */
function riserParts(step: IStepGeometry, stringerType: IStringerType, materialId: string): IRawPart[] {
    const { yTop, yBot } = riserSpanY(step);
    const [start, end]: [number, number] = riserExtent(step, stringerType);
    const width: number =
        step.isWinder && step.winderCorners ? longestEdge(winderRiserBoardOutline(step, step.winderCorners)) : end - start;
    const boards: { width: number; height: number }[] = [{ width, height: yTop - yBot }];
    if (!step.isWinder && step.exitRiserHeight && step.exitRiserHeight > 0) {
        const [exitStart, exitEnd]: [number, number] = exitRiserExtent(step, stringerType);
        boards.push({ width: exitEnd - exitStart, height: step.exitRiserHeight });
    }
    return boards
        .filter((board: { width: number; height: number }) => board.width >= MIN_SIZE && board.height >= MIN_SIZE)
        .map((board: { width: number; height: number }) => ({
            materialId,
            kind: EPartKind.Riser,
            thickness: RISER_BOARD_THICKNESS,
            outline: rectangle(board.width, board.height),
        }));
}

function profileToPart(profile: readonly IProfilePoint[]): IPartVertex[] {
    return normalizeOutline(profile.map((point: IProfilePoint) => ({ x: point.run, y: point.y, bulge: 0 })));
}

/** A turn-zone board unrolled along `from` → `to`: its length on the heights at both ends. */
function blockOutline(block: NonNullable<IStringerGeometry['block']>): IPartVertex[] {
    const length: number = planLength(planSub(block.to, block.from));
    return normalizeOutline([
        { x: 0, y: block.bottomFrom, bulge: 0 },
        { x: length, y: block.bottomTo, bulge: 0 },
        { x: length, y: block.topTo, bulge: 0 },
        { x: 0, y: block.topFrom, bulge: 0 },
    ]);
}

/** A straight sloped plank between two points: its true length on its width. */
function plankOutline(first: IVec3, last: IVec3, width: number): IPartVertex[] {
    return rectangle(Math.hypot(last.x - first.x, last.y - first.y, last.z - first.z), width);
}

/** The flat outlines of one stringer, split the way the 3D splits it. */
function stringerOutlines(stringer: IStringerGeometry, stringerType: IStringerType): IPartVertex[][] {
    if (stringer.block) {
        return [blockOutline(stringer.block)];
    }
    if (stringer.bottom) {
        return [profileToPart(tetivaProfile(stringer.points, stringer.bottom, kosourRunAxis(stringer.points).runOf))];
    }
    const { points, width } = stringer;
    if (stringerType !== 'kosour' && points.length === 3) {
        return [plankOutline(points[0], points[1], width), plankOutline(points[1], points[2], width)];
    }
    if (points.length > 2) {
        return [profileToPart(kosourProfile(points, width, stringer.bottomSlope, stringer.runSpan))];
    }
    return [plankOutline(points[0], points[points.length - 1], width)];
}

function stringerParts(stringers: readonly IStringerGeometry[], config: IStairConfig): IRawPart[] {
    // The spiral's only stringer is its steel pole, not a board.
    if (config.type === 'spiral') {
        return [];
    }
    return stringers
        .filter((stringer: IStringerGeometry) => stringer.points.length >= 2)
        .flatMap((stringer: IStringerGeometry) =>
            stringerOutlines(stringer, config.stringerType).map((outline: IPartVertex[]) => ({
                outline,
                kind: EPartKind.Stringer,
                materialId: config.stringerMaterialId,
                thickness: stringer.thickness,
            }))
        );
}

/** The turn's support posts (SC-CALC-093): a square bar of the load-bearing species, as long as the post is tall. */
function supportPostParts(supports: ISupportPosts | undefined, config: IStairConfig): IRawPart[] {
    return (supports?.posts ?? [])
        .filter((post: IBalusterGeometry) => post.height >= MIN_SIZE)
        .map((post: IBalusterGeometry) => ({
            outline: rectangle(post.height, supports?.width ?? 0),
            kind: EPartKind.SupportPost,
            materialId: config.stringerMaterialId,
            thickness: supports?.width ?? 0,
        }));
}

/**
 * The boards a calculated stair is cut from — treads, landings, risers and stringers — with alike
 * ones (same kind, species, thickness and outline, flipped included) counted once.
 */
export function productionParts(result: IStairCalculationResult, config: IStairConfig): IProductionPart[] {
    const steps: IStepGeometry[] = result.geometry.steps;
    const raw: IRawPart[] = [
        ...steps.map((step: IStepGeometry) => treadPart(step, config)),
        ...(config.risersEnabled
            ? steps.flatMap((step: IStepGeometry) => riserParts(step, config.stringerType, config.riserMaterialId))
            : []),
        ...stringerParts(result.geometry.stringers, config),
        ...supportPostParts(result.geometry.supports, config),
    ];
    const groups: Map<string, IProductionPart> = new Map<string, IProductionPart>();
    raw.forEach((part: IRawPart) => {
        const key: string = [part.kind, part.materialId, part.thickness, outlineKey(part.outline)].join('/');
        const group: IProductionPart | undefined = groups.get(key);
        if (group) {
            group.count += 1;
        } else {
            groups.set(key, { ...part, count: 1 });
        }
    });
    return [...groups.values()].sort((a: IProductionPart, b: IProductionPart) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
}
